import React, { memo } from 'react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar, ExternalLink, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { MeetingPrepButton } from '../../components/SmartMeetingPrep';

// Memoized Meeting Card
const MeetingCard = memo(({ meeting, onClick }) => (
  <div 
    className="p-4 border rounded-lg hover:shadow-sm hover:border-blue-200 transition-all cursor-pointer"
    onClick={() => onClick(meeting)}
    data-testid={`meeting-card-${meeting.id}`}
  >
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-medium">{meeting.title}</h4>
      <Badge className={
        meeting.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
        meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
        'bg-slate-100 text-slate-600'
      }>{meeting.status}</Badge>
    </div>
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <span className="flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {format(new Date(meeting.start_time), 'MMM d, yyyy h:mm a')}
      </span>
      {meeting.meeting_link && (
        <a 
          href={meeting.meeting_link} 
          target="_blank" 
          rel="noreferrer" 
          className="flex items-center gap-1 text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" /> Join
        </a>
      )}
    </div>
    {/* Meeting Prep Button */}
    <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
      <MeetingPrepButton meeting={meeting} contactId={meeting.contact_id} />
    </div>
  </div>
));

MeetingCard.displayName = 'MeetingCard';

// Main MeetingsTab component
const MeetingsTab = memo(({ meetings, onMeetingClick }) => {
  if (meetings.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-muted-foreground">No meetings scheduled</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[550px]">
      <div className="p-4">
        <div className="space-y-3">
          {meetings.map(meeting => (
            <MeetingCard 
              key={meeting.id} 
              meeting={meeting} 
              onClick={onMeetingClick}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
});

MeetingsTab.displayName = 'MeetingsTab';

export default MeetingsTab;
